import React, { RefObject, useCallback, useMemo, useState } from 'react'
import { useLingui } from '@lingui/react/macro'

import {
  BedDoubleIcon,
  Contact01Icon,
  NoteDoneIcon
} from '@hugeicons/core-solid-rounded'

import { Step, ValueType } from '@/components/ui/ProgressStepper'
import { RoomsForm } from '../components/RoomsForm'
import { CustomerForm } from '../components/CustomerForm'
import { SummaryForm } from '../components/SummaryForm'

export interface FormProps {
  ref: RefObject<HTMLFormElement | null>
  onDone: () => void
}

export type BillingStep = Step & {
  Component: React.ComponentType<FormProps>
}

export function useSteps() {
  const { t } = useLingui()

  const steps = useMemo(
    () =>
      [
        {
          value: 'rooms',
          label: t`Choose Rooms`,
          icon: BedDoubleIcon,
          Component: RoomsForm
        },
        {
          value: 'customer',
          label: t`Billing Address`,
          icon: Contact01Icon,
          Component: CustomerForm
        },
        {
          value: 'summary',
          label: t`Summary`,
          icon: NoteDoneIcon,
          Component: SummaryForm
        }
      ] as BillingStep[],
    [t]
  )

  const [step, setStep] = useState<ValueType>(steps[0].value)

  const index = useMemo(
    () => steps.findIndex(s => s.value === step),
    [steps, step]
  )

  const increaseStep = useCallback(
    (inc: number) => {
      const newIndex = Math.min(Math.max(index + inc, 0), steps.length - 1)
      const value = steps[newIndex].value

      setStep(value)
    },
    [index, steps, setStep]
  )

  return {
    step,
    index,
    steps,
    isLast: index === steps.length - 1,
    setStep,
    increaseStep
  }
}
